import type { ReactNode } from 'react';
import { ContactNameFields } from './ContactNameCells';
import {
  ContactTextField,
  DeleteContactButton,
  GroupControl,
  PrimaryControl,
  useContactRowWrites,
  type ContactRowProps,
} from './ContactManagerRow';
import { INLINE_FIELD_LABELS } from './rules';

/**
 * One contact in the contacts manager, stacked, for below `md` — the same
 * fields and actions as `ContactManagerRow` (the desktop `<TR>`), in the same
 * order: Primary | Full / First / Last name | Title | Email (inline edit + the
 * `mailto:` button) | Phone | Mobile | Group | Delete.
 *
 * ⛔ **Every cell and every write is the ROW's, imported, never re-inlined.**
 * `useContactRowWrites` owns the PATCHes, the refusal wording and the primary /
 * group in-flight state; `PrimaryControl`, `ContactTextField`, `GroupControl`
 * and `DeleteContactButton` own the controls. A second copy here would agree
 * on the day it was written and drift on the first correction — and what would
 * drift is which key a save sends.
 *
 * ⚠️ **Touch targets are `max-sm:h-11`.** `Input` and `Combobox` carry it
 * already; the shared row controls use `Button size="sm"` (`max-sm:h-10`) and
 * the inline-edit triggers are bare buttons with no height at all, so the
 * wrappers below raise every button and link inside them to 44px below `sm`
 * rather than forking those controls.
 */

/** Raises every inline trigger, button and link inside to a 44px touch target below `sm`. */
const TOUCH =
  'max-sm:[&_button]:min-h-11 max-sm:[&_a]:min-h-11 max-sm:[&_a]:min-w-11';

export function ContactManagerCard({
  contact,
  groups,
  isPrimary,
  canEditContact,
  canEditCompany,
  canDelete,
  onSaved,
  onMakePrimary,
  onDelete,
}: ContactRowProps) {
  const writes = useContactRowWrites(contact, onSaved, onMakePrimary);
  const showPrimary = isPrimary || canEditCompany;

  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-md border border-border bg-surface p-3">
      {(showPrimary || canDelete) && (
        <div className="flex min-w-0 items-start justify-between gap-2">
          <div className={`min-w-0 ${TOUCH}`}>
            <PrimaryControl
              contact={contact}
              isPrimary={isPrimary}
              canEditCompany={canEditCompany}
              state={writes.primary}
              onMakePrimary={() => void writes.makePrimary()}
            />
          </div>
          {canDelete && (
            <div className="shrink-0 max-sm:[&_button]:min-h-11 max-sm:[&_button]:min-w-11">
              <DeleteContactButton contact={contact} onDelete={onDelete} />
            </div>
          )}
        </div>
      )}

      <div className={TOUCH}>
        <ContactNameFields contact={contact} canEdit={canEditContact} onSave={writes.savePatch} />
      </div>

      <div className="grid min-w-0 gap-2">
        <Field label={INLINE_FIELD_LABELS.title}>
          <ContactTextField
            contact={contact}
            field="title"
            canEditContact={canEditContact}
            saveField={writes.saveField}
          />
        </Field>
        <Field label={INLINE_FIELD_LABELS.email}>
          <ContactTextField
            contact={contact}
            field="email"
            canEditContact={canEditContact}
            saveField={writes.saveField}
          />
        </Field>
        <Field label={INLINE_FIELD_LABELS.phone}>
          <ContactTextField
            contact={contact}
            field="phone"
            canEditContact={canEditContact}
            saveField={writes.saveField}
          />
        </Field>
        <Field label={INLINE_FIELD_LABELS.mobile}>
          <ContactTextField
            contact={contact}
            field="mobile"
            canEditContact={canEditContact}
            saveField={writes.saveField}
          />
        </Field>
        <Field label="Group">
          <GroupControl
            contact={contact}
            groups={groups}
            canEditContact={canEditContact}
            action={writes.groupAction}
            onChange={(value) => void writes.changeGroup(value)}
          />
        </Field>
      </div>
    </div>
  );
}

/** A label over its value — the card's one layout unit, matching `ContactNameFields`. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={`grid min-w-0 gap-0.5 ${TOUCH}`}>
      <span className="text-xs text-fg-muted">{label}</span>
      <div className="min-w-0 text-sm text-fg">{children}</div>
    </div>
  );
}
